import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, UserIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { studentAttendanceAPI, studentAPI } from '../../services/api';

const AttendanceRecords = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(20);
  // Set default date to today
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'range'
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');

  // Fetch attendance records
  const { data: attendanceData, isLoading } = useQuery({
    queryKey: ['attendance-records', currentPage, selectedDate, startDate, endDate, selectedStudent, viewMode],
    queryFn: () => {
      // Use single date or date range based on view mode
      const start = viewMode === 'single' ? selectedDate : (startDate || null);
      const end = viewMode === 'single' ? selectedDate : (endDate || null);

      return studentAttendanceAPI
        .getAttendanceRecords(
          currentPage * itemsPerPage,
          itemsPerPage,
          start,
          end,
          selectedStudent || null
        )
        .then(res => res.data);
    },
  });

  // Fetch students for filter
  const { data: studentsData } = useQuery({
    queryKey: ['students-list'],
    queryFn: () => studentAPI.getAll(0, 1000, true).then(res => res.data),
  });

  const attendances = attendanceData?.attendances || [];
  const total = attendanceData?.total || 0;
  const students = Array.isArray(studentsData) ? studentsData : (studentsData?.students || []);

  // For single day view, merge all students with their attendance records
  const getDisplayRecords = () => {
    // In single day view without student filter, show all students
    if (viewMode === 'single' && !selectedStudent) {
      // Create a map of attendance records by student_id
      const attendanceMap = {};
      attendances.forEach(record => {
        attendanceMap[record.student_id] = record;
      });

      // Create records for all students
      const allRecords = students.map(student => {
        const studentId = student.student_id;
        const attendanceRecord = attendanceMap[studentId];

        if (attendanceRecord) {
          // Student has attendance record - return it
          return attendanceRecord;
        } else {
          // Student doesn't have attendance record - mark as absent
          return {
            _id: `absent-${studentId}-${selectedDate}`,
            student_id: studentId,
            student_name: `${student.first_name} ${student.last_name}`,
            check_in_phone: student.phone,
            attendance_date: selectedDate,
            check_in_time: null,
            check_out_time: null,
            learning_summary: null,
            status: 'Absent'
          };
        }
      });

      return allRecords;
    }

    // For other modes, return attendance records as-is
    return attendances;
  };

  const displayRecords = getDisplayRecords();
  const displayTotal = viewMode === 'single' && !selectedStudent ? students.length : total;

  const formatDate = (dateString) => {
    // Ensure the date string is treated as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatTime = (dateString) => {
    // Ensure the date string is treated as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const calculateWorkHours = (checkIn, checkOut) => {
    if (!checkOut) return '-';
    const diff = new Date(checkOut) - new Date(checkIn);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const clearFilters = () => {
    setViewMode('single');
    setSelectedDate(getTodayDate());
    setStartDate('');
    setEndDate('');
    setSelectedStudent('');
    setCurrentPage(0);
  };

  const goToPreviousDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
    setCurrentPage(0);
  };

  const goToNextDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
    setCurrentPage(0);
  };

  const goToToday = () => {
    setSelectedDate(getTodayDate());
    setCurrentPage(0);
  };

  const isToday = selectedDate === getTodayDate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/student-attendance"
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4 text-sm"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Attendance Management
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Attendance Records</h1>
            <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">
              View and filter student attendance records
            </p>
          </div>
          <Link
            to={`/student-attendance/analytics?viewMode=${viewMode}&startDate=${viewMode === 'single' ? selectedDate : startDate}&endDate=${viewMode === 'single' ? selectedDate : endDate}`}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm whitespace-nowrap"
          >
            <ChartBarIcon className="h-5 w-5 mr-2" />
            View Analytics
          </Link>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-900/30 to-gray-800 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <svg className="h-5 w-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {/* View Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3 flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2 text-blue-400" />
              View Mode
            </label>
            <div className="grid grid-cols-2 gap-3 p-1 bg-gray-900 rounded-lg">
              <button
                onClick={() => {
                  setViewMode('single');
                  setCurrentPage(0);
                }}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                  viewMode === 'single'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-300 hover:bg-gray-700'
                }`}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Single Day
              </button>
              <button
                onClick={() => {
                  setViewMode('range');
                  setCurrentPage(0);
                }}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                  viewMode === 'range'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-300 hover:bg-gray-700'
                }`}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Date Range
              </button>
            </div>
          </div>

          {/* Single Day View */}
          {viewMode === 'single' && (
            <div className="space-y-4">
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Selected Date
                </label>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <button
                    onClick={goToPreviousDay}
                    className="w-full sm:w-auto px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2 hover:scale-105"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>

                  <div className="flex-1 w-full relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-400 pointer-events-none" />
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setCurrentPage(0);
                      }}
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium hover:bg-gray-650 transition-colors"
                    />
                  </div>

                  <button
                    onClick={goToNextDay}
                    disabled={isToday}
                    className="w-full sm:w-auto px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:scale-105 disabled:hover:scale-100"
                  >
                    Next
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {!isToday && (
                  <div className="mt-3">
                    <button
                      onClick={goToToday}
                      className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all text-sm font-medium shadow-lg hover:shadow-blue-500/30 flex items-center justify-center gap-2"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Jump to Today
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Date Range View */}
          {viewMode === 'range' && (
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
                    <svg className="h-4 w-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Start Date
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-400 pointer-events-none" />
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setCurrentPage(0);
                      }}
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent hover:bg-gray-650 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
                    <svg className="h-4 w-4 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    End Date
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-400 pointer-events-none" />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setCurrentPage(0);
                      }}
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent hover:bg-gray-650 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Student Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
              <UserIcon className="h-4 w-4 mr-2 text-purple-400" />
              Filter by Student
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-400 pointer-events-none" />
              <select
                value={selectedStudent}
                onChange={(e) => {
                  setSelectedStudent(e.target.value);
                  setCurrentPage(0);
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none cursor-pointer hover:bg-gray-650 transition-colors"
              >
                <option value="">All Students</option>
                {students.map((stu) => (
                  <option key={stu.student_id} value={stu.student_id}>
                    {stu.first_name} {stu.last_name} ({stu.student_id})
                  </option>
                ))}
              </select>
              <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(selectedStudent || (viewMode === 'range' && (startDate || endDate)) || (viewMode === 'single' && selectedDate !== getTodayDate())) && (
            <div className="pt-2 border-t border-gray-700">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 text-white rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">
            {displayTotal} Record{displayTotal !== 1 ? 's' : ''}
          </h2>
        </div>

        {displayRecords.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-white">No records found</h3>
            <p className="mt-1 text-sm text-gray-400">
              Try adjusting your filters or check back later
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Student Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Check In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Check Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Learning Summary
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {displayRecords.map((record) => (
                    <tr key={record._id || record.id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {formatDate(record.attendance_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{record.student_name || record.student_id}</div>
                        <div className="text-xs text-gray-400">{record.student_id}</div>
                        <div className="text-xs text-gray-500">{record.check_in_phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {record.status === 'Absent' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-300 border border-red-700">
                            Absent
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/50 text-green-300 border border-green-700">
                            Present
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">
                        {record.check_in_time ? formatTime(record.check_in_time) : <span className="text-gray-500">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {record.check_out_time ? (
                          <span className="text-red-400">{formatTime(record.check_out_time)}</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {calculateWorkHours(record.check_in_time, record.check_out_time)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {record.learning_summary ? (
                          <div className="space-y-1">
                            <details className="cursor-pointer">
                              <summary className="text-blue-400 hover:text-blue-300">
                                View Learning Summary
                              </summary>
                              <div className="mt-2 p-3 bg-gray-700 rounded text-xs">
                                <p className="text-gray-300">{record.learning_summary}</p>
                              </div>
                            </details>
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-700">
              {displayRecords.map((record) => (
                <div key={record._id || record.id} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-sm font-medium text-white">{record.student_name || record.student_id}</div>
                      <div className="text-xs text-gray-400">{record.student_id}</div>
                      <div className="text-xs text-gray-400">{formatDate(record.attendance_date)}</div>
                    </div>
                    <div className="text-right">
                      {record.status === 'Absent' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-300 border border-red-700">
                          Absent
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/50 text-green-300 border border-green-700">
                          Present
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div>
                      <span className="text-gray-400">Check In:</span>
                      <div className="text-green-400">{record.check_in_time ? formatTime(record.check_in_time) : '-'}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Check Out:</span>
                      <div className="text-red-400">
                        {record.check_out_time ? formatTime(record.check_out_time) : '-'}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm mb-2">
                    <span className="text-gray-400">Hours:</span>
                    <span className="text-white ml-2">{calculateWorkHours(record.check_in_time, record.check_out_time)}</span>
                  </div>
                  {record.learning_summary && (
                    <div className="mt-3">
                      <details className="cursor-pointer">
                        <summary className="text-blue-400 text-sm">View Learning Summary</summary>
                        <div className="mt-2 p-3 bg-gray-700 rounded text-xs">
                          <p className="text-gray-300">{record.learning_summary}</p>
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {displayTotal > itemsPerPage && !(viewMode === 'single' && !selectedStudent) && (
              <div className="bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-700">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={(currentPage + 1) * itemsPerPage >= total}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-400">
                      Showing{' '}
                      <span className="font-medium">{currentPage * itemsPerPage + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min((currentPage + 1) * itemsPerPage, total)}
                      </span>{' '}
                      of <span className="font-medium">{total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={currentPage === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={(currentPage + 1) * itemsPerPage >= total}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceRecords;
